/**
 * Encounter Utilities
 *
 * Shared utilities for working with FHIR Encounters.
 * Centralizes common patterns used across multiple pages.
 */

import { appointmentsApi } from '@/lib/api/appointments';
import { medplumResourcesApi } from '@/lib/api/medplum-resources';
import { apiClient } from '@/lib/api/client';
import { createComponentLogger } from '@/lib/utils/logger';

const logger = createComponentLogger('EncounterUtils');

/**
 * Reference to a FHIR resource with optional display name
 */
export interface FhirReference {
  reference?: string;
  display?: string;
}

/**
 * Extract Encounter ID from supportingInformation array
 * Returns the ID portion of the Encounter reference if found
 */
export function extractEncounterIdFromSupportingInfo(
  supportingInformation?: FhirReference[]
): string | null {
  if (!supportingInformation) return null;

  const encounterRef = supportingInformation.find((info) =>
    info.reference?.startsWith('Encounter/')
  );

  if (encounterRef?.reference) {
    return encounterRef.reference.split('/')[1] || null;
  }

  return null;
}

/**
 * Fetch Encounter linked to an Appointment via supportingInformation
 * Returns the Encounter object if found, null otherwise
 */
export async function getEncounterForAppointment(appointmentId: string) {
  try {
    const appointment = await appointmentsApi.getAppointment(appointmentId);
    const encounterId = extractEncounterIdFromSupportingInfo(appointment.supportingInformation);

    if (!encounterId) {
      logger.log('No encounter found in appointment supportingInformation:', appointmentId);
      return null;
    }

    logger.log('Found encounter from appointment:', encounterId);
    return medplumResourcesApi.getEncounter(encounterId);
  } catch (error) {
    logger.error('Error fetching encounter for appointment:', error);
    return null;
  }
}

/**
 * Get the consultation navigation path for an encounter
 * Always returns a path using encounter ID (encounters should be created before consultation)
 */
export function getConsultationPath(encounterId?: string | null): string | null {
  if (encounterId) {
    return `/consultation/${encounterId}`;
  }
  return null;
}

/**
 * Link an Encounter and Appointment bidirectionally:
 *   - encounter.appointment → Appointment/{id}
 *   - appointment.supportingInformation → Encounter/{id}
 */
export async function linkEncounterToAppointment(
  encounterId: string,
  appointmentId: string
): Promise<boolean> {
  try {
    await Promise.all([
      medplumResourcesApi.updateEncounter(encounterId, {
        appointment: [{ reference: `Appointment/${appointmentId}` }],
      }),
      appointmentsApi.updateAppointment(appointmentId, {
        encounterId: encounterId,
      }),
    ]);
    logger.log('Encounter and appointment linked bidirectionally:', { encounterId, appointmentId });
    return true;
  } catch (error) {
    logger.error('Failed to link encounter to appointment:', error);
    return false;
  }
}

/**
 * Update appointment status (non-blocking, logs errors)
 */
export async function updateAppointmentStatusSafe(
  appointmentId: string,
  status: 'arrived' | 'fulfilled' | 'cancelled' | 'noshow'
): Promise<void> {
  try {
    await appointmentsApi.updateAppointment(appointmentId, { status });
    logger.log(`Appointment ${appointmentId} status updated to ${status}`);
  } catch (error) {
    logger.error(`Failed to update appointment ${appointmentId} status to ${status}:`, error);
    // Don't throw - this is a non-critical operation
  }
}

/**
 * Start a consultation for a patient via the BFF.
 * Finds or creates today's Appointment + Encounter atomically server-side,
 * surfaces kiosk ClinicalImpression if fresh (≤24 h).
 *
 * Replaces the pattern of createAppointment + createEncounterForConsultation
 * in all CDSS entry points (Dashboard, Appointments, Records, PatientRecord).
 */
export async function startConsultationForPatient(patientId: string): Promise<{
  encounterId: string;
  appointmentId: string;
  clinicalImpressionId?: string;
}> {
  const PRANIK_HMIS_V2 = `${import.meta.env.VITE_AUTH_API_URL}/v2`;
  const response = await apiClient.post<{
    success: boolean;
    encounterId?: string;
    appointmentId?: string;
    clinicalImpressionId?: string;
    error?: string;
  }>('/kiosk/start-consultation', {
    body: { patientId },
    baseUrl: PRANIK_HMIS_V2,
  });

  const result = response.data;

  if (!result.success || !result.encounterId || !result.appointmentId) {
    throw new Error(
      result.error ?? 'startConsultation failed: missing encounterId or appointmentId'
    );
  }

  logger.log('Consultation started:', {
    encounterId: result.encounterId,
    appointmentId: result.appointmentId,
    clinicalImpressionId: result.clinicalImpressionId,
  });

  return {
    encounterId: result.encounterId,
    appointmentId: result.appointmentId,
    clinicalImpressionId: result.clinicalImpressionId,
  };
}
